import { useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, Check } from "lucide-react";

/**
 * Share button — uses the native Web Share API on mobile/supported browsers,
 * falls back to copy-to-clipboard on desktop. When users share the link,
 * the OG meta tags in index.html render a rich preview card with the
 * hero image, title, and description.
 */
export default function ShareButton({
  title = "Cam's Koori Art — CamOriginal",
  text = "Original paintings, prints, apparel, murals & Paint-Your-Own kits from Barkindji artist Cameron Boyle.",
  url = typeof window !== "undefined" ? window.location.origin : "https://camoriginal.com",
  variant = "primary",
  className = "",
  label = "Share CKA",
  testid = "share-button",
  iconOnly = false,
}) {
  const [copied, setCopied] = useState(false);

  const doShare = async (e) => {
    // Prevent parent handlers (e.g. gallery card onClick) from firing
    e?.stopPropagation?.();
    e?.preventDefault?.();
    // Prefer native share sheet (mobile, PWAs, Safari, Chrome Android, etc.)
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        // User cancelled or share not permitted — fall through to copy
        if (err?.name !== "AbortError") {
          console.warn("Native share failed, falling back to copy:", err);
        } else {
          return; // user cancelled — do nothing
        }
      }
    }
    // Fallback: copy the URL to the clipboard
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — paste anywhere to share");
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.warn("Clipboard write failed:", err);
      toast.error("Couldn't copy — long-press the address bar to share");
    }
  };

  if (iconOnly) {
    return (
      <button
        type="button"
        data-testid={testid}
        onClick={doShare}
        className={`inline-flex items-center justify-center w-9 h-9 border border-white/20 bg-[#0A0A0A]/80 backdrop-blur text-white/90 hover:border-[#FF4500] hover:text-[#FF4500] transition-colors ${className}`}
        aria-label={label}
        title={label}
      >
        {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
      </button>
    );
  }

  const baseClasses = variant === "ghost"
    ? "btn-ghost"
    : "btn-primary";

  return (
    <button
      type="button"
      data-testid={testid}
      onClick={doShare}
      className={`${baseClasses} ${className}`}
      aria-label={label}
    >
      {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
      <span>{copied ? "Copied" : label}</span>
    </button>
  );
}
